import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import {
  hasManualSpacing,
  listParagraphs,
  looksLikeManualList,
  paragraphsWithStyle,
  readDocument,
} from "@/office/word/document";

export const lektionAbsaetzeAbschluss: Lesson = {
  id: "abschlussuebung",
  title: "Abschlussübung: Ein aufgeräumtes Protokoll",
  summary: "Ausrichtung, Abstände, Einzüge und Listen in einem Dokument zusammenführen.",
  durationMinutes: 12,
  learningGoals: [
    "Absatzformatierung vollständig ohne Behelfslösungen einsetzen",
    "Ein Dokument auf typische Formatierungsfehler prüfen",
  ],
  steps: [
    {
      id: "protokoll",
      title: "Das Protokoll aufbauen",
      explanation: [
        "Zum Abschluss baust du ein kurzes Besprechungsprotokoll – am besten in einem " +
          "neuen Dokument (Strg + N). Es geht diesmal nicht um den Inhalt, sondern darum, " +
          "dass keine einzige Behelfslösung mehr darin steckt.",
        "Konkret heißt das: keine Leerzeilen als Abstand, keine Leerzeichenketten oder " +
          "Tabulatoren als Einzug, keine getippten Striche als Aufzählung.",
      ],
      keyPoints: [
        "Zentrierte Titelzeile, linksbündiger Fließtext.",
        "Abstand über die Absatzeinstellung, nicht über Enter.",
        "Echte Liste für die Beschlüsse.",
      ],
      task: {
        instruction:
          "Erstelle ein Protokoll mit zentrierter Titelzeile, mindestens drei " +
          "Fließtextabsätzen mit Absatzabstand und einer echten Liste mit mindestens drei " +
          "Punkten. Es darf keine Leerzeile, keinen getippten Aufzählungsstrich und keinen " +
          "Tabulator-Einzug enthalten.",
        hint: "Arbeite die Kriterienliste von oben nach unten ab und prüfe zwischendurch immer wieder.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.filledParagraphs.length < 5) {
            return fail(
              "Für die Abschlussübung braucht es etwas mehr Inhalt.",
              "Titelzeile, drei Absätze Fließtext und eine Liste mit drei Punkten.",
            );
          }

          const zentriert = snapshot.filledParagraphs.filter(
            (absatz) => absatz.format.alignment === "Centered",
          );
          const listen = listParagraphs(snapshot);
          const standard = paragraphsWithStyle(snapshot, "Normal").filter(
            (absatz) => absatz.list === null && absatz.format.alignment !== "Centered",
          );
          const mitAbstand = standard.filter(
            (absatz) => typeof absatz.format.spaceAfter === "number" && absatz.format.spaceAfter > 0,
          );
          const leerzeilen = snapshot.paragraphs.filter((absatz) => absatz.text.length === 0).length;
          const nachgetippt = snapshot.filledParagraphs.filter(looksLikeManualList).length;
          const tabulatoren = snapshot.filledParagraphs.filter(hasManualSpacing).length;

          return fromCriteria(
            [
              criterion("Eine zentrierte Titelzeile", zentriert.length >= 1),
              criterion(
                `Mindestens drei Fließtextabsätze (gefunden: ${standard.length})`,
                standard.length >= 3,
              ),
              criterion(
                "Alle Fließtextabsätze haben einen Absatzabstand",
                standard.length > 0 && mitAbstand.length === standard.length,
              ),
              criterion(
                `Eine echte Liste mit mindestens drei Punkten (gefunden: ${listen.length})`,
                listen.length >= 3,
              ),
              criterion(`Keine Leerzeilen (gefunden: ${leerzeilen})`, leerzeilen === 0),
              criterion(`Keine nachgetippten Aufzählungen (gefunden: ${nachgetippt})`, nachgetippt === 0),
              criterion(
                `Keine Tabulatoren oder Leerzeichenketten (gefunden: ${tabulatoren})`,
                tabulatoren === 0,
              ),
            ],
            {
              success: "Sauber – dieses Dokument hält auch, wenn sich der Inhalt ändert.",
              failure: "Fast geschafft. Diese Punkte fehlen noch:",
              hint: "Blende mit Strg + Umschalt + 8 alle Steuerzeichen ein. Leerzeilen, Tabulatoren und getippte Striche werden dann sofort sichtbar.",
            },
          );
        },
      },
    },
    {
      id: "rueckblick",
      title: "Rückblick",
      explanation: [
        "Damit ist die zweite Formatierungsebene abgeschlossen. Auffällig ist das Muster, " +
          "das sich durch das ganze Modul zieht: Für fast jede Behelfslösung – Leerzeile, " +
          "Leerzeichenkette, getippter Strich – gibt es in Word eine richtige Einstellung, " +
          "die dasselbe Ergebnis liefert und dabei stabil bleibt.",
        "Und wie im Modul davor gilt: Sobald du eine dieser Einstellungen zum zweiten Mal " +
          "vergibst, gehört sie in eine Formatvorlage.",
      ],
      keyPoints: [
        "Ausrichtung: Fließtext linksbündig, Blocksatz nur mit Silbentrennung.",
        "Abstand zwischen Absätzen über „Abstand nach“, nie über Enter.",
        "Einzüge über die Einzugseinstellung, nie über Leerzeichen.",
        "Listen über die Listenschaltflächen, nie über getippte Zeichen.",
      ],
    },
  ],
};
